import React, {useEffect} from "react"

import {Row, Col} from "react-bootstrap"
import {Input, TextArea} from "@irati/ui/src/FormItem"
import OccasionsSelect from "@irati-web/fields/occasions/OccasionsSelect"
import {CompanyValuesSelect} from "@irati-web/fields/companyValues"
import {EMOJI} from "@irati/ui/src/Reactions/constants"
import authentication from "@irati/auth"
import isShareableWithTeams from "@irati/recognition/utils/isShareableWithTeams"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import {Share} from "@irati-web/fields/poll"
import getSharingOptions, {PRIVATE_OPTION, TEAMS} from "@irati/recognition/constants/share"
import Validation from "@irati/utils/validation"
import Scheduler from "@irati-web/recognition/src/components/Popup/CreateRecognition/Scheduler"
import Spacing from "@irati/ui/src/Spacing"
import {useRewardsDistribution, useRewardsDistributionActions} from "../../../stores/DistributeReward/hooks"
import {DistributeRewardRecipient, DistributeRewardImportType} from "../../../enums/DistributeRewardTypes"
import DistributeRewardStepIndicator from "./DistributeRewardStepIndicator"

interface Props extends InjectIntl {}

const MESSAGE_MAX_LENGTH = 2000
const TEMPLATE_MESSAGE_MAX_LENGTH = 256
const TITLE_MAX_LENGTH = 50

const DistributeRewardStep2: React.FC<Props> = ({formatMessage}) => {
  const payload = useRewardsDistribution()
  const {dispatch} = useRewardsDistributionActions()
  const canShareWithEveryone = authentication.apps("recognition.recognition.everyone")
  const user = authentication.user()
  let canBeSharedWithTeams = isShareableWithTeams(user, payload.recognition)
  const sharingOptions = getSharingOptions(formatMessage, canShareWithEveryone, canBeSharedWithTeams)
  const canSchedule =
    payload.recipientType === DistributeRewardRecipient.EMPLOYEE && payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV

  useEffect(() => {
    if (payload.userEmails.length === payload.destinationUsers.length) {
      dispatch({type: "SET_STEP", payload: "recipients"})
    }
  }, [])

  const handleUpdateShareChange = (recognitionObj: any, key: string, value: any) => {
    let newRecognition = {
      ...recognitionObj,
      [key]: value
    }

    canBeSharedWithTeams = isShareableWithTeams(user, newRecognition)

    if (!canBeSharedWithTeams && newRecognition.share.value === TEAMS) {
      newRecognition = {
        ...newRecognition,
        share: PRIVATE_OPTION(formatMessage)
      }
    }

    dispatch({
      type: "SET_RECOGNITION",
      payload: {
        recognition: newRecognition
      }
    })
  }

  const maxMessageLength = payload.rewardTemplate ? TEMPLATE_MESSAGE_MAX_LENGTH : MESSAGE_MAX_LENGTH
  return (
    <>
      <DistributeRewardStepIndicator currentStep={2} />
      {payload.rewardTemplate && (
        <Spacing marginBottom={3}>
          <Row>
            <Input
              name="reward-template-title"
              value={payload.rewardTemplateTitle}
              hint={formatMessage({id: "wallet.reward.template.label.title.hint"})}
              onChange={(title) => dispatch({type: "SET_REWARD_TEMPLATE_TITLE", payload: {title}})}
              validation={payload.rewardTemplateTitle && Validation.string("platform.defaultValidation.required").max(TITLE_MAX_LENGTH)}
              label={"wallet.reward.template.title"}
              placeholder="wallet.reward.template.placeholder.title"
            />
          </Row>
        </Spacing>
      )}
      <Spacing marginBottom={2}>
        <Row>
          <TextArea
            label="wallet.reward.admin.distribute.messageBox.label"
            placeholder="wallet.reward.distribute.messageBox.placeholder"
            name="messageBox"
            onChange={(message) => dispatch({type: "SET_MESSAGE", payload: {message}})}
            textReplaceTypes={[EMOJI]}
            value={payload.message}
            maxLength={maxMessageLength}
            validation={Validation.string("platform.defaultValidation.required").max(maxMessageLength)}
            rows={5}
          />
        </Row>
      </Spacing>

      {authentication.apps("recognition") &&
        !payload.rewardTemplate &&
        payload.recipientType === DistributeRewardRecipient.EMPLOYEE &&
        payload.rewardImportType === DistributeRewardImportType.MANUAL_ENTER && (
          <Row>
            <Share
              options={sharingOptions}
              poll={payload.recognition}
              onChange={handleUpdateShareChange}
              hasTeams={canBeSharedWithTeams}
            />
            <OccasionsSelect
              selectedOption={payload.recognition.occasionId}
              onChange={(occasionId) =>
                dispatch({
                  type: "SET_RECOGNITION",
                  payload: {
                    recognition: {
                      occasionId
                    }
                  }
                })
              }
            />
            <CompanyValuesSelect item={payload.recognition} onChange={handleUpdateShareChange} />
          </Row>
        )}

      {canSchedule && (
        <>
          <Spacing marginBottom={2} />
          <Row>
            <Col xs={12}>
              <Scheduler
                defaultValue={{
                  timezone: payload.schedule?.timezone ?? "",
                  date: (payload.schedule?.isScheduled && payload.schedule?.utc) || ""
                }}
                onChange={(utc, timezone) => dispatch({type: "SET_SCHEDULE", payload: {utc, timezone}})}
                onScheduleOptionChange={(isScheduled) => dispatch({type: "SET_SCHEDULE", payload: {isScheduled}})}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  )
}

export default injectIntl(DistributeRewardStep2)
